import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
  AbstractControl,
} from '@angular/forms';
import { ActivatedRoute, RouterModule, Router } from '@angular/router';
import { ManageUsersService } from '../../../manage-users/services/manage-users.service';
import { DestroyBase } from '../../../../shared/base-classes/destroy-component-base';
import { takeUntil } from 'rxjs';
import { CreateUser } from '../../../manage-users/models/create-user';
import { SkeletonComponent } from '../../../../shared/components/skeleton/skeleton.component';

@Component({
  selector: 'app-user-self-signup',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterModule, SkeletonComponent],
  templateUrl: './user-self-signup.component.html',
  styleUrl: './user-self-signup.component.scss',
})
export class UserSelfSignupComponent extends DestroyBase implements OnInit {
  private fb = inject(FormBuilder);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  userService = inject(ManageUsersService);

  signupForm: FormGroup;
  isSubmitting = false;
  showPassword = false;
  showConfirmPassword = false;
  invitationError = false;
  errorMessage = '';
  user: CreateUser | null = null;
  invitationUrlId: string | null = null;

  // Success state properties
  showSuccessMessage = false;
  successMessage = '';
  isRedirecting = false;
  isLoading = false;

  constructor() {
    super();
    this.signupForm = this.fb.group(
      {
        email: [
          { value: '', disabled: true },
          [Validators.required, Validators.email],
        ],
        firstName: [
          '',
          [
            Validators.required,
            Validators.minLength(2),
            Validators.maxLength(50),
          ],
        ],
        lastName: ['', [Validators.maxLength(50)]],
        password: [
          '',
          [
            Validators.required,
            Validators.minLength(8),
            Validators.pattern(
              /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
            ),
          ],
        ],
        confirmPassword: ['', [Validators.required]],
      },
      {
        validators: this.passwordMatchValidator,
      },
    );
  }

  ngOnInit(): void {
    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      const invitationUrlId = params.get('id');
      this.isLoading = true;
      if (invitationUrlId) {
        this.invitationUrlId = invitationUrlId;
        this.invitationError = false;
        this.errorMessage = '';
        this.userService
          .getUserInvitaionDetails(invitationUrlId)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (invitation) => {
              if (invitation.accepted_on_utc) {
                this.invitationError = true;
                this.errorMessage =
                  'This invitation link has already been used.';
              }
              this.signupForm.patchValue({
                email: invitation.email,
              });
              this.isLoading = false;
            },
            error: (error) => {
              this.invitationError = true;
              this.errorMessage = this.getErrorMessage(error);
              this.isLoading = false;
            },
          });
      } else {
        this.invitationError = true;
        this.errorMessage =
          'Invalid invitation link. Please check your invitation URL.';
        this.isLoading = false;
      }
    });
  }

  passwordMatchValidator(
    control: AbstractControl,
  ): Record<string, boolean> | null {
    const password = control.get('password');
    const confirmPassword = control.get('confirmPassword');

    if (
      password &&
      confirmPassword &&
      password.value !== confirmPassword.value
    ) {
      return { passwordMismatch: true };
    }
    return null;
  }

  togglePasswordVisibility(field: 'password' | 'confirmPassword'): void {
    if (field === 'password') {
      this.showPassword = !this.showPassword;
    } else {
      this.showConfirmPassword = !this.showConfirmPassword;
    }
  }

  onSubmit(): void {
    if (this.signupForm.valid) {
      this.isSubmitting = true;

      // Create user object from form values
      this.user = {
        first_name: this.signupForm.get('firstName')?.value,
        last_name: this.signupForm.get('lastName')?.value,
        password: this.signupForm.get('password')?.value,
      };

      if (this.invitationUrlId && this.user) {
        this.errorMessage = '';
        this.userService
          .createUser(this.user, this.invitationUrlId)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.isSubmitting = false;
              this.showSuccessMessage = true;
              this.successMessage =
                'Account created successfully! Redirecting to login...';
              this.isRedirecting = true;

              // Hide any previous error messages
              this.errorMessage = '';
              this.invitationError = false;

              // Redirect to login page after 2 seconds
              setTimeout(() => {
                this.router.navigate(['/login']);
              }, 2000);
            },
            error: (error) => {
              console.error('Error creating user:', error);
              this.isSubmitting = false;
              this.errorMessage = this.getErrorMessage(error);
            },
          });
      } else {
        this.isSubmitting = false;
        this.errorMessage = 'Invalid invitation link. Please try again.';
      }
    } else {
      this.markFormGroupTouched();
    }
  }

  private markFormGroupTouched(): void {
    Object.keys(this.signupForm.controls).forEach((key) => {
      const control = this.signupForm.get(key);
      control?.markAsTouched();
    });
  }

  getFieldError(fieldName: string): string {
    const field = this.signupForm.get(fieldName);
    if (field?.errors && field.touched) {
      if (field.errors['required'])
        return `${this.getFieldLabel(fieldName)} is required`;
      if (field.errors['email']) return 'Please enter a valid email address';
      if (field.errors['minlength'])
        return `${this.getFieldLabel(fieldName)} must be at least ${field.errors['minlength'].requiredLength} characters`;
      if (field.errors['maxlength'])
        return `${this.getFieldLabel(fieldName)} must not exceed ${field.errors['maxlength'].requiredLength} characters`;
      if (field.errors['pattern'] && fieldName === 'password') {
        return 'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character';
      }
    }

    if (
      this.signupForm.errors?.['passwordMismatch'] &&
      fieldName === 'confirmPassword' &&
      this.signupForm.get('confirmPassword')?.touched
    ) {
      return 'Passwords do not match';
    }

    return '';
  }

  private getFieldLabel(fieldName: string): string {
    const labels: Record<string, string> = {
      firstName: 'First name',
      lastName: 'Last name',
      password: 'Password',
      confirmPassword: 'Confirm password',
      email: 'Email',
    };
    return labels[fieldName] || fieldName;
  }

  private getErrorMessage(error: HttpErrorResponse | unknown): string {
    const errObj = (error as HttpErrorResponse)?.error;
    if (errObj?.detail) {
      return errObj.detail;
    }
    if (errObj?.message) {
      return errObj.message;
    }
    if (typeof errObj === 'string' && errObj) {
      return errObj;
    }
    return 'An error occurred while processing your request. Please try again or contact support.';
  }

  // Password validation helper methods
  hasLowercase(): boolean {
    return /[a-z]/.test(this.signupForm.get('password')?.value || '');
  }

  hasUppercase(): boolean {
    return /[A-Z]/.test(this.signupForm.get('password')?.value || '');
  }

  hasNumber(): boolean {
    return /\d/.test(this.signupForm.get('password')?.value || '');
  }

  hasSpecialChar(): boolean {
    return /[@$!%*?&]/.test(this.signupForm.get('password')?.value || '');
  }

  hasMinLength(): boolean {
    return (this.signupForm.get('password')?.value || '').length >= 8;
  }
}
